import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import UpgradeState from '../ui/UpgradeState';

// Wraps a route element. While the snapshot is still loading the children
// render (the server will 402 anyway if they act too early); once loaded, a
// feature outside the plan shows the upgrade state instead of a page that
// looks broken. `featureName` is the already-translated human name.
const FeatureGate = ({ feature, featureName, children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, plan, status, subscribed } = useEntitlements();

  // While the first snapshot is loading, don't mount the page: it would fire
  // its own requests and get a 402 before we know it's gated. idle/error →
  // the server remains the judge, render normally.
  if (status === 'loading') {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (status !== 'ready' && status !== 'refreshing') return children;
  if (can(feature)) return children;

  const isAdmin = localStorage.getItem('userRole') === 'admin';
  const noSubscription = !subscribed && !plan;
  return (
    <UpgradeState
      title={noSubscription
        ? t('Your company has no active subscription', { defaultValue: 'Your company has no active subscription' })
        : t("{{feature}} isn't included in your plan", { feature: featureName, defaultValue: `${featureName} isn't included in your plan` })}
      description={plan
        ? t('Your company is on {{plan}}. Your existing data is untouched either way.', { plan: plan.name, defaultValue: `Your company is on ${plan.name}. Your existing data is untouched either way.` })
        : (noSubscription
          ? t('Choose a plan to unlock {{feature}}. Your existing data is untouched either way.', { feature: featureName, defaultValue: `Choose a plan to unlock ${featureName}. Your existing data is untouched either way.` })
          : undefined)}
      action={isAdmin
        ? { label: t('See plans', { defaultValue: 'See plans' }), onClick: () => navigate('/subscription') }
        : { label: t('Ask your admin', { defaultValue: 'Ask your admin' }), onClick: () => navigate('/dashboard') }}
    />
  );
};

export default FeatureGate;
